import { ReactNode, Suspense, useEffect, useRef, useState } from "react";

interface DeferredSectionProps {
  children: ReactNode;
  /** Reserved height while the section has not mounted yet — prevents layout shift. */
  minHeight?: number;
  id?: string;
  className?: string;
}

/**
 * Renders its children only once the placeholder scrolls near the viewport.
 * Keeps below-the-fold homepage sections out of the critical path while
 * reserving vertical space so nothing shifts when they mount.
 */
const DeferredSection = ({ children, minHeight = 480, id, className }: DeferredSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} id={id} className={className} style={visible ? undefined : { minHeight }}>
      {visible ? <Suspense fallback={<div style={{ minHeight }} />}>{children}</Suspense> : null}
    </div>
  );
};

export default DeferredSection;
